import { useCallback } from 'react';

// Module-level cache for cross-component sharing
// Key: callId, Value: { data, timestamp }
const recordingCache = new Map();

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached recording data if not expired
 * @param {string} callId
 * @returns {object|null} Cached data or null if expired/missing
 */
export const getCached = (callId) => {
  const entry = recordingCache.get(callId);
  if (!entry) return null;

  const now = Date.now();
  if (now - entry.timestamp > CACHE_TTL_MS) {
    recordingCache.delete(callId);
    return null;
  }

  return entry.data;
};

/**
 * Store recording data in cache
 * @param {string} callId
 * @param {object} data
 */
export const setCache = (callId, data) => {
  recordingCache.set(callId, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Clear cached entry for a callId (used for error recovery)
 * @param {string} callId
 */
export const clearCache = (callId) => {
  recordingCache.delete(callId);
};

/**
 * Hook wrapper for convenient use in components
 * @returns {{ getCached, setCache, clearCache }}
 */
export const useRecordingCache = () => {
  const get = useCallback((callId) => getCached(callId), []);
  const set = useCallback((callId, data) => setCache(callId, data), []);
  const clear = useCallback((callId) => clearCache(callId), []);

  return {
    getCached: get,
    setCache: set,
    clearCache: clear
  };
};

export default useRecordingCache;
